import type { OrderLineDiscountStaffRecord } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NavioOverlayActions, NoddiButton, NoddiInput, NoddiSpinner, NoddiTextArea } from 'noddi-ui-web';
import { useState } from 'react';

type Props = {
  discountId: number;
  onCancel: () => void;
  onSuccess: () => void;
};

/** Dialog-agnostic edit-discount content: fetches the staff record, pre-fills, and PATCHes. */
export const EditDiscountContent = ({ discountId, onCancel, onSuccess }: Props) => {
  const {
    data: discount,
    isPending,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getOrderLineDiscount,
    input: { discountId }
  });

  if (isPending) {
    return (
      <div className='flex justify-center py-6'>
        <NoddiSpinner />
      </div>
    );
  }

  if (error || !discount) {
    return <p className='py-4 text-destructive'>Could not load discount.</p>;
  }

  return <EditDiscountForm discount={discount} onCancel={onCancel} onSuccess={onSuccess} />;
};

type FormProps = {
  discount: OrderLineDiscountStaffRecord;
  onCancel: () => void;
  onSuccess: () => void;
};

const EditDiscountForm = ({ discount, onCancel, onSuccess }: FormProps) => {
  const [name, setName] = useState(discount.name);
  const [amountGross, setAmountGross] = useState<number | ''>(Math.abs(discount.amountGross.amount));
  const [description, setDescription] = useState(discount.description ?? '');
  const [reason, setReason] = useState(discount.reason ?? '');

  const { mutateAsync: patchDiscount, isPending: isSaving } = noddiAsync.usePatch({
    type: URLKeys.patchOrderLineDiscount,
    queryConfig: {
      onSuccess: async () => {
        await invalidateQueryExactMatch({
          urlKey: URLKeys.getOrderLineDiscount,
          input: { discountId: discount.id }
        });
        onSuccess();
      }
    }
  });

  const isValid = amountGross !== '' && amountGross > 0 && Boolean(name) && Boolean(reason);

  const handleSubmit = async () => {
    if (amountGross === '' || amountGross <= 0 || !name || !reason) {
      return;
    }
    await patchDiscount({ discountId: discount.id, name, amountGross, description, reason });
  };

  return (
    <div className='grid grid-cols-1 gap-4'>
      <NoddiInput
        className='min-w-0'
        type='text'
        name='name'
        label='Name'
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <NoddiInput
        className='min-w-0'
        type='number'
        name='amountGross'
        min={0}
        label='Total discount amount, greater than 0'
        value={amountGross}
        onChange={(e) => setAmountGross(e.target.value ? Number(e.target.value) : '')}
      />
      <NoddiTextArea
        className='min-w-0'
        name='description'
        label='Description for customer'
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <NoddiTextArea
        className='min-w-0'
        name='reason'
        label='Reason (internal)'
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <NavioOverlayActions>
        <NoddiButton variant='outline' onClick={onCancel}>
          Cancel
        </NoddiButton>
        {/* oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleSubmit(); React discards the returned promise, so a rejection is unhandled unless the body catches */}
        <NoddiButton onClick={handleSubmit} loading={isSaving} disabled={!isValid}>
          Save
        </NoddiButton>
      </NavioOverlayActions>
    </div>
  );
};
